import { useState, useEffect, useCallback } from 'react';
import { getVapidPublicKey, savePushSubscription, removePushSubscription } from '../services/api';

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * Hook per gestire le notifiche push via Web Push API.
 * - Verifica il supporto del browser
 * - Gestisce subscribe/unsubscribe
 * - Sincronizza la subscription con il backend
 */
export function usePushNotifications() {
  const [permission, setPermission] = useState<PushPermissionState>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  // Check current state on mount
  useEffect(() => {
    if (!isSupported) {
      setPermission('unsupported');
      return;
    }

    setPermission(Notification.permission as PushPermissionState);

    // Check if already subscribed
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    });
  }, [isSupported]);

  /**
   * Richiedi permesso e registra la subscription push.
   */
  const subscribe = useCallback(async (deviceName?: string) => {
    if (!isSupported) {
      setError('Notifiche push non supportate dal browser');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Get VAPID public key from server
      const { publicKey } = await getVapidPublicKey();
      if (!publicKey) {
        setError('Web Push non configurato sul server');
        return false;
      }

      // 2. Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm as PushPermissionState);
      if (perm !== 'granted') {
        setError('Permesso notifiche negato');
        return false;
      }

      // 3. Subscribe to push via browser
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      // 4. Send subscription to backend
      const subJson = subscription.toJSON();
      await savePushSubscription({
        endpoint: subJson.endpoint!,
        p256dh: subJson.keys?.p256dh ?? '',
        auth: subJson.keys?.auth ?? '',
        deviceName: deviceName ?? navigator.userAgent.substring(0, 80),
        userAgent: navigator.userAgent,
      });

      setIsSubscribed(true);
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore attivazione push';
      setError(msg);
      console.error('[Push] Subscribe error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  /**
   * Annulla la subscription push.
   */
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        // Remove from backend
        await removePushSubscription({ endpoint: subscription.endpoint });
        // Unsubscribe from browser
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore disattivazione push';
      setError(msg);
      console.error('[Push] Unsubscribe error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
  };
}

/**
 * Converte una chiave VAPID base64url in Uint8Array per PushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
